import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Check, ChevronDown, Loader2 } from 'lucide-react';
import {
  Badge,
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
  ScrollArea,
  cn,
} from '@scio3/ui';

const ITEM_HEIGHT = 36;
const OVERSCAN = 5;
/** Floor for the default (trigger-matched) popover width, so a narrow trigger (e.g. a 200px table
 *  cell in SAML group-mapping) still opens a readable list. Wide triggers grow past it. */
const MIN_WIDTH_PX = 400;

/**
 * The opt-in last row of the list — "commit what I typed", for fields whose legal values aren't
 * limited to the option list.
 *
 * ⛔ The row is a REAL, keyboard-reachable option, never `disabled`, even when the typed value is
 * gated behind an upgrade. cmdk's selectable selector is
 * `[cmdk-item]:not([aria-disabled="true"])` and `CommandItem` spreads its own `aria-disabled` over
 * whatever you pass, so `disabled` is the only way to mark a row — and it also removes the row from
 * arrow-key nav and suppresses `onSelect`. A gated row that can't be reached can't explain itself.
 * Keep it enabled and let `onSelect` open the gate; `icon` + `hint` carry the locked meaning.
 */
export interface ComboboxCreateRow {
  /** Row label. Receives the raw trimmed query so the caller can interpolate it. */
  label: (typed: string) => ReactNode;
  /** Muted second line — where a gated row explains what would unlock it. */
  hint?: ReactNode;
  icon?: ReactNode;
  /** Fired with the raw trimmed query AFTER the popover closes, so a dialog raised from here
   *  isn't fighting the Popover's dismissable layer for focus. */
  onSelect: (typed: string) => void;
}

export interface SearchableComboboxProps<T> {
  value: string;
  items: T[];
  getId: (item: T) => string;
  getLabel: (item: T) => string;
  /**
   * Optional tag rendered after the label (list row AND trigger) — e.g. "System" on a built-in role
   * so it can be told apart from a custom role carrying the same name (#2133). Not searched on.
   */
  getBadge?: (item: T) => string | undefined;
  /**
   * Draw a row (and the chosen value in the trigger) yourself, instead of label + badge. `inTrigger`
   * tells the two apart, so a row can carry detail (a description) the closed control leaves out.
   * Pair with `itemHeight` when rows are taller than one line.
   */
  renderItem?: (item: T, ctx: { inTrigger: boolean }) => ReactNode;
  /** Row height in px for the virtualized list (default 36, one line). */
  itemHeight?: number;
  onChange: (id: string) => void;
  loading?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  /** Fixed popover width in px. When omitted (default), the popover flexes to the trigger width
   *  (`--radix-popover-trigger-width`, floored at MIN_WIDTH_PX) so it lines up with the control
   *  that opened it while staying readable for narrow triggers. */
  widthPx?: number;
  className?: string;
  /** Disables the trigger (blocks mouse AND keyboard open), matching a native disabled control. */
  disabled?: boolean;
  /** id for the trigger — lets a sibling `<label htmlFor>` associate with the combobox. */
  id?: string;
  /** Opt-in "use what I typed" row. Rendered only while the query is non-empty and matches no
   *  option — an exact hit needs no create row, and an empty query has nothing to create. */
  createRow?: ComboboxCreateRow;
  /** Per-item decoration: an icon before the label, a badge after it, a muted second line under
   *  it. Used for BOTH a locked option (badge = the add-on marker, hint = why) and a free one
   *  (badge only) — the combobox draws what it is handed and reads no meaning into it.
   *
   *  ⛔ A decorated item is still an ordinary, selectable item. `onChange` fires for it and the
   *  CALLER decides what that means. Never mark one disabled: cmdk drops `aria-disabled` items
   *  from keyboard nav, which would put the only explanation out of reach. */
  getItemMeta?: (item: T) => { icon?: ReactNode; badge?: ReactNode; hint?: ReactNode } | undefined;
  /** Pinned to the bottom of the open popover, OUTSIDE the option list — so it is visible with an
   *  empty query (when no create row exists yet) and may contain real interactive controls, which
   *  a descendant of `role="option"` may not. */
  footer?: ReactNode;
  /** Render a value that matches no item as the trigger label, instead of falling back to the
   *  placeholder. Implied by `createRow`; pass it directly when the field CAN hold an unlisted
   *  value but the row itself is suppressed — a field that is immutable after create is disabled,
   *  so without this its saved custom value reads as an empty field. */
  allowsUnlistedValue?: boolean;
  /** Per-option `data-testid`. The list is virtualized, so an option far down the list is only in
   *  the DOM once the search narrows it into view — a test has to type before it clicks. */
  getItemTestId?: (item: T) => string;
  /** No check-mark column: rows start at the left edge. For pickers whose trigger already shows the
   *  choice (the teamspace picker, Jessica 2026-10-20), where the reserved space pushed every row
   *  right for a mark only one row ever shows. */
  hideCheck?: boolean;
  'data-testid'?: string;
}

/**
 * Generic single-select combobox: search-filtered, virtualized list of items.
 * Each row truncates long labels to a single line (the wrapper here owns the
 * full DOM chain — unlike shadcn Select, ellipsis renders correctly).
 *
 * On open, scrolls the virtualizer to the currently-selected row so the
 * user sees their choice in context. Skipped when search is active.
 */
export function SearchableCombobox<T>({
  value,
  items,
  getId,
  getLabel,
  getBadge,
  renderItem,
  itemHeight = ITEM_HEIGHT,
  onChange,
  loading,
  placeholder,
  searchPlaceholder,
  emptyMessage,
  widthPx,
  className,
  disabled,
  id,
  createRow,
  getItemMeta,
  footer,
  allowsUnlistedValue,
  getItemTestId,
  hideCheck = false,
  'data-testid': testId,
}: SearchableComboboxProps<T>) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const selected = items.find((item) => getId(item) === value);
  // An unlisted value is legal but absent from `items`, so `selected` is undefined and the trigger
  // would fall through to the placeholder — i.e. claim the field is empty when it isn't. Show the
  // raw value instead; it IS the label for a value the user typed.
  // ⛔ NOT gated on `!loading`, and that is the whole of the "Loading…" flash. Opening a saved asset
  // rendered the handle, then replaced it with "Loading…" while the option list fetched, then put
  // the same handle back — three states for one unchanged value. The guard existed to let a nicer
  // label win once `items` arrived, but it paid for that by claiming we did not know a value we were
  // already displaying. A value we can show always outranks a word about waiting.
  const createdValue = !selected && (createRow || allowsUnlistedValue) && value ? value : '';
  // `loading` outranks the placeholder: `items` is empty mid-fetch, so `selected` is undefined and
  // a field that HAS a saved value would otherwise render its placeholder — i.e. claim nothing is
  // selected. Matches the non-searchable branch of the same manifest field (smallapp SelectField
  // passes t('Loading...') as SelectValue's placeholder), so flipping a manifest's `searchable`
  // doesn't change the wording the user sees.
  const triggerLabel = selected
    ? getLabel(selected)
    : createdValue || (loading ? t('Loading...') : placeholder || t('Select...'));

  // Manual filter so the virtualizer drives the visible row set.
  // cmdk's built-in filter is disabled via shouldFilter={false} below.
  const typed = searchText.trim();
  const search = typed.toLowerCase();
  const filteredItems = search
    ? items.filter((item) => getLabel(item).toLowerCase().includes(search))
    : items;

  // An exact hit on a label or id needs no create row — it would just duplicate the option above.
  const showCreateRow =
    !!createRow &&
    !loading &&
    !!typed &&
    !items.some(
      (item) => getLabel(item).toLowerCase() === search || getId(item).toLowerCase() === search,
    );

  const virtualizer = useVirtualizer({
    count: filteredItems.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => itemHeight,
    // The list's 4px breathing room lives INSIDE the scroll (Jessica, 2026-10-20): as padding on
    // the group it framed the scroll area, so rows were clipped 4px short of the top and bottom.
    paddingStart: 4,
    paddingEnd: 4,
    overscan: OVERSCAN,
  });

  const virtualItems = virtualizer.getVirtualItems();

  /**
   * Close when the trigger scrolls out of its own scroll container.
   *
   * ⛔ The popover tracks its anchor even after a scroll container has CLIPPED it, so inside a
   * scrolling dialog the list follows the field right out of the dialog and floats over unrelated
   * UI. Measured in the smallapp config dialog: scroll 700px and the trigger sits at y=-302 with
   * the list at y=-262 — 316px ABOVE the dialog, still visible and still interactive.
   *
   * Radix's `hideWhenDetached` is the one-prop version of this and is WRONG here: it only sets
   * `visibility: hidden`, leaving an open popover that still owns focus, and in jsdom every rect is
   * 0×0 so Floating UI reads the anchor as detached and hides the list in every unit test.
   *
   * Closing is also the better answer than hiding: the user scrolled the field away, they are done
   * with it. Small scrolls keep it open — this fires only once the trigger is fully past the
   * container's edge.
   */
  useEffect(() => {
    if (!open) return;
    const trigger = triggerRef.current;
    if (!trigger) return;
    let node: HTMLElement | null = trigger.parentElement;
    while (node && node !== document.body) {
      const overflowY = getComputedStyle(node).overflowY;
      if (overflowY === 'auto' || overflowY === 'scroll') break;
      node = node.parentElement;
    }
    if (!node || node === document.body) return;
    const scroller = node;
    const onScroll = (): void => {
      const t = trigger.getBoundingClientRect();
      const s = scroller.getBoundingClientRect();
      if (t.bottom <= s.top || t.top >= s.bottom) setOpen(false);
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => scroller.removeEventListener('scroll', onScroll);
  }, [open]);

  // On open, scroll to the currently-selected item so the user sees their
  // choice in context instead of starting at row 0. Skipped when searching.
  useEffect(() => {
    if (!open || !scrollElement || search) return;
    const index = selected ? filteredItems.findIndex((it) => getId(it) === getId(selected)) : -1;
    virtualizer.scrollToIndex(index > 0 ? index : 0, { align: 'center' });
  }, [open, scrollElement, search, selected, filteredItems, virtualizer, getId]);

  return (
    <Popover
      open={disabled ? false : open}
      onOpenChange={(next) => {
        if (disabled) return;
        setOpen(next);
        if (!next) setSearchText('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          id={id}
          data-testid={testId}
          // `trigger`, not `outline`: this Button IS a form control, and has to be
          // indistinguishable from the Input/Select it sits beside. `outline` is the
          // standalone-button look — lighter border (muted-foreground/20 vs border-input),
          // rounded-sm instead of rounded-md, a filled `bg-card`, and a hover that fills the
          // background instead of darkening the border. Next to a plain select in the same form
          // that reads as a different kind of control.
          variant="trigger"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn('w-full cursor-pointer justify-between', className)}
        >
          {/* Muted while it IS a placeholder, exactly like the controls beside it: Input has
              `placeholder:text-muted-foreground` and SelectTrigger `data-[placeholder]:…`, both of
              which come free from a real placeholder attribute. This trigger is a Button rendering
              plain text, so neither applies and the hint read at full `text-foreground` — i.e. it
              looked like a chosen value. MultiSelectCombobox already renders its empty state this
              way; this matches it. */}
          {selected && renderItem ? (
            <span className="flex min-w-0 flex-1 items-center">
              {renderItem(selected, { inTrigger: true })}
            </span>
          ) : (
            <span className={cn('truncate', !selected && !createdValue && 'text-muted-foreground')}>
              {triggerLabel}
            </span>
          )}
          {!renderItem && selected && getBadge?.(selected) && (
            <Badge
              variant="secondary"
              className="ml-2 shrink-0 px-1.5 py-0 text-[10px] font-medium leading-4"
              data-testid="role-option-system-badge"
            >
              {getBadge(selected)}
            </Badge>
          )}
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        // Default: match the trigger width (flexible), floored at MIN_WIDTH_PX so a narrow trigger
        // still opens a readable list. A fixed `widthPx` opts out entirely. Inline `max(var())` is
        // used rather than a Tailwind `[--var]` arbitrary class (which doesn't emit `var(...)`).
        className="p-0"
        style={
          widthPx
            ? { width: widthPx }
            : { width: `max(${MIN_WIDTH_PX}px, var(--radix-popover-trigger-width))` }
        }
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={searchPlaceholder ?? t('Search...')}
            value={searchText}
            onValueChange={setSearchText}
          />
          <CommandList className="max-h-none overflow-visible">
            {loading && (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
            {/* "No results" is only true when there is also nothing to DO about it — with a create
                row present it reads as a dead end above a live option. */}
            {!loading && filteredItems.length === 0 && !showCreateRow && (
              <CommandEmpty>{emptyMessage ?? t('No results found')}</CommandEmpty>
            )}
            {!loading && filteredItems.length > 0 && (
              <CommandGroup className="px-1 py-0">
                <ScrollArea
                  type="auto"
                  className="max-h-[300px]"
                  viewportRef={setScrollElement}
                  viewportClassName="max-h-[300px]"
                  onWheel={(e) => e.stopPropagation()}
                >
                  <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
                    {virtualItems.map((virtualItem) => {
                      const item = filteredItems[virtualItem.index];
                      if (!item) return null;
                      const id = getId(item);
                      const label = getLabel(item);
                      const badge = getBadge?.(item);
                      const gate = getItemMeta?.(item);
                      return (
                        <CommandItem
                          key={id}
                          value={`${label} ${id}`}
                          onSelect={() => {
                            onChange(id);
                            setOpen(false);
                            setSearchText('');
                          }}
                          className={cn('cursor-pointer', gate && 'items-start')}
                          data-locked={gate ? 'true' : undefined}
                          data-testid={getItemTestId?.(item)}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 12,
                            height: virtualItem.size,
                            transform: `translateY(${virtualItem.start}px)`,
                          }}
                        >
                          {!hideCheck && (
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4 shrink-0',
                                value === id ? 'opacity-100' : 'opacity-0',
                              )}
                            />
                          )}
                          {gate?.icon && (
                            <span className="mr-2 flex h-4 w-4 shrink-0 items-center justify-center">
                              {gate.icon}
                            </span>
                          )}
                          {/* THREE extension APIs, all with live consumers, so all three render:
                              `renderItem` takes the whole row when given (the Group Security
                              pickers); otherwise `getItemMeta` supplies main's icon/badge/hint gate
                              (smallapp SelectField) and `getBadge` the system-role chip
                              (SamlSettingsPage). Collapsing to either one alone silently blanks the
                              other's options. */}
                          {renderItem ? (
                            <span className="min-w-0 flex-1">
                              {renderItem(item, { inTrigger: false })}
                            </span>
                          ) : (
                            <span className="min-w-0 flex-1">
                              <span className="flex items-center">
                                <span className="truncate">{label}</span>
                                {gate?.badge}
                                {badge && (
                                  <Badge
                                    variant="secondary"
                                    className="ml-2 shrink-0 px-1.5 py-0 text-[10px] font-medium leading-4"
                                    data-testid="role-option-system-badge"
                                  >
                                    {badge}
                                  </Badge>
                                )}
                              </span>
                              {gate?.hint && (
                                <span className="block truncate text-xs text-muted-foreground">
                                  {gate.hint}
                                </span>
                              )}
                            </span>
                          )}
                        </CommandItem>
                      );
                    })}
                  </div>
                </ScrollArea>
              </CommandGroup>
            )}
            {/* Outside the virtualizer on purpose: an action row that scrolls out of view is an
                action row nobody finds. Last in DOM order, so cmdk's arrow-key nav reaches it
                after every option. */}
            {showCreateRow && createRow && (
              <CommandGroup
                className={cn(filteredItems.length > 0 && 'border-t border-border')}
                data-testid="combobox-create-group"
              >
                <CommandItem
                  value={`__create__ ${typed}`}
                  onSelect={() => {
                    // Close FIRST: Radix Popover and a Dialog raised from `onSelect` are both
                    // dismissable layers competing for focus, and the popover's FocusScope yanks
                    // focus back out of a dialog that opened while it was still mounted.
                    setOpen(false);
                    setSearchText('');
                    createRow.onSelect(typed);
                  }}
                  className="cursor-pointer items-start"
                  data-testid="combobox-create-item"
                >
                  <span className="mr-2 flex h-4 w-4 shrink-0 items-center justify-center">
                    {createRow.icon}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{createRow.label(typed)}</span>
                    {createRow.hint && (
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        {createRow.hint}
                      </span>
                    )}
                  </span>
                </CommandItem>
              </CommandGroup>
            )}
          </CommandList>
          {footer}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
